<script setup lang="ts">
import { ref } from 'vue';
import UiIcon, { type UiIconProps } from './UiIcon.vue';
import UiIconButton from './UiButton/UiIconButton.vue';
import UiFieldContainer from './UiFieldContainer.vue';

export interface FormInputFieldProps {
    icon?: UiIconProps['name'];
    clearable?: boolean;
    placeholder?: string | undefined;
    name?: string | undefined;  
    id?: string | undefined;
    class?: string | string[] | Record<string, boolean>;
    required?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    type?: "text" | "search" | "password" | "email" | "number";
    min?: number | undefined;
    max?: number | undefined;
    step?: number | undefined;
    pattern?: string | undefined;
    size?: 'md' | 'lg'
    hint?: string | undefined;
    error?: string | undefined;
    invalid?: boolean | undefined;
    ariaLabel?: string | undefined;
}

const props = withDefaults(
    defineProps<FormInputFieldProps>(),
    {
        clearable: false,
        class: '',
        type: 'text',
        required: false,
        readonly: false,
        disabled: false,
        min: undefined,
        max: undefined,
        step: undefined,
        pattern: undefined,
        size: 'md',
        hint: undefined,
        error: undefined,
        invalid: undefined,
    }
)

const model = defineModel<string>(); 
const inputRef = ref<HTMLInputElement>();

const emit = defineEmits<{
    (e: 'validate'): void
}>()

function handleInput(event: Event) {
    const target = event.target as HTMLInputElement;
    model.value = target.value;
}

function clearInput() {
    model.value = '';
    focusInput();
}

const isFocused = ref(false);

function focusInput() {
    isFocused.value = true;
    inputRef.value?.focus();
}

function blurInput() {
    isFocused.value = false;
    inputRef.value?.blur();
    emit('validate')
}

</script>

<template>
    <UiFieldContainer :class="props.class" :isFocused="isFocused" :size="props.size" :invalid="props.invalid" :hint="props.hint" :error="props.error" :describedByIdBase="props.id || props.name" :disabled="props.disabled" @focus="focusInput" @blur="blurInput">
        <template v-if="props.icon">
            <UiIcon :name="props.icon" :class="[props.size === 'lg' ? 'w-6 h-6' : 'w-5 h-5', 'text-se-gray-700']" />
        </template>
        <input 
            :placeholder="props.placeholder"
            :name="props.name"
            :id="props.id || props.name"
            :value="model"
            class="text-md min-w-0 flex-1 se-font-input font-archivo"
            ref="inputRef"
            @input="handleInput"
            @blur="blurInput"
            :type="props.type"
            :required="props.required"
            :readonly="props.readonly"
            :disabled="props.disabled"
            :min="props.min"
            :max="props.max"
            :step="props.step"
            :pattern="props.pattern"
            :aria-invalid="props.invalid ? 'true' : undefined"
            :aria-label="props.ariaLabel"
            :aria-describedby="props.error ? (props.id || props.name) + '-error' : (props.hint ? (props.id || props.name) + '-hint' : undefined)"
        />
        <UiIconButton
            v-if="props.clearable && model && model.length > 0"
            icon="close"
            aria-label="Clear input"
            :disabled="props.disabled || props.readonly"
            @mousedown.prevent
            @click.stop="clearInput"
        />
    </UiFieldContainer>
</template>

<style scoped>
input[type="search"]::-webkit-search-cancel-button,
input[type="search"]::-webkit-search-decoration {
    -webkit-appearance: none;
}
</style>
